import { useEffect } from 'react';
import { getStableFeatureId, normalizeParcelDetails } from './parcelUtils';
import { pickProviderForLatLng } from './BoundariesControl';

/**
 * Interactive parcel inspect layer.
 *
 * On top of the always-on WMS raster overlay, this hook fetches a small set of
 * vector polygons in the current viewport from each state's WFS / ArcGIS REST
 * query endpoint and renders them through a `google.maps.Data` layer. Vector
 * geometry gives us proper hit-testing for hover & click while the raster WMS
 * keeps the line work crisp at every zoom.
 *
 * Behaviours owned by this hook:
 *   - Lazy creation of the data layer on first run
 *   - Hover cursor affordance (no fill paint on hover, per UX feedback)
 *   - Click → open Property Info panel + sticky yellow highlight, with a
 *     priority rule that lets user-drawn shapes intercept clicks first
 *   - Reverse-geocoded street address fetched async after the panel opens
 *   - Idle-driven refetch as the user pans/zooms (only at zoom ≥ 17)
 *   - Out-of-order WFS guard so stale responses don't overwrite fresher ones
 *   - Toggling clickability on/off whenever a drawing tool is active so the
 *     data layer doesn't eat events meant for the drawing manager
 *   - Cleanup of the idle listener + features on unmount or capability flip
 *
 * The hook does not own React state — it writes the inspected parcel back via
 * `setPropertyInfo` and reads selection state via refs supplied by MapView.
 * Component-defined callbacks (`findUserShapeAt`, `selectShapeById`,
 * `deselectAllShapes`) are passed through `callbacksRef` so the hook always
 * calls the latest version without re-subscribing the click handler on every
 * render.
 */
export function useParcelInspect({
  mapReady,
  mapRef,
  parcelDataLayerRef,
  parcelFetchSeqRef,
  selectedParcelIdRef,
  boundariesEnabled,
  capabilities,
  drawingMode,
  customTool,
  setPropertyInfo,
  callbacksRef,
}) {
  // -------------------------------------------------------------------------
  // (1) Data-layer lifecycle + idle-driven WFS refetch
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || !window.google?.maps?.Data) return;

    if (!parcelDataLayerRef.current) {
      const layer = new window.google.maps.Data({ map: mapRef.current });
      layer.setStyle({
        fillColor: '#FFEB3B',
        fillOpacity: 0,
        strokeColor: '#FFEB3B',
        strokeWeight: 0,
        strokeOpacity: 0,
        clickable: true,
      });
      const applySelectedStyle = (feature) => {
        layer.overrideStyle(feature, {
          fillColor: '#FFEB3B',
          fillOpacity: 0.35,
          strokeColor: '#F59E0B',
          strokeWeight: 3,
          strokeOpacity: 1,
        });
      };
      layer.__applySelectedStyle = applySelectedStyle;

      layer.addListener('mouseover', () => {
        if (mapRef.current?.getDiv) {
          mapRef.current.getDiv().style.cursor = 'pointer';
        }
      });
      layer.addListener('mouseout', () => {
        if (mapRef.current?.getDiv) {
          mapRef.current.getDiv().style.cursor = '';
        }
      });
      layer.addListener('click', async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        // Priority: if the click falls inside a user-drawn shape, route to
        // that shape so the user's work stays on top of the cadastre overlay.
        const hit = callbacksRef.current?.findUserShapeAt?.(e.latLng);
        if (hit) {
          callbacksRef.current?.selectShapeById?.(hit.id);
          return;
        }
        const clickedId = getStableFeatureId(e.feature);
        const prevId = selectedParcelIdRef.current;
        if (prevId != null && prevId !== clickedId) {
          layer.forEach((f) => {
            if (getStableFeatureId(f) === prevId) {
              try { layer.revertStyle(f); } catch { /* feature may already be torn down */ }
            }
          });
        }
        selectedParcelIdRef.current = clickedId;
        applySelectedStyle(e.feature);

        let areaM2 = 0;
        try {
          const sph = window.google.maps.geometry.spherical;
          const geom = e.feature.getGeometry();
          const type = geom.getType();
          if (type === 'Polygon') {
            geom.getArray().forEach((ring, i) => {
              const path = [];
              ring.forEachLatLng((ll) => path.push(ll));
              if (path.length < 3) return;
              const a = sph.computeArea(path);
              areaM2 += (i === 0 ? a : -a);
            });
          } else if (type === 'MultiPolygon') {
            geom.getArray().forEach((poly) => {
              poly.getArray().forEach((ring, i) => {
                const path = [];
                ring.forEachLatLng((ll) => path.push(ll));
                if (path.length < 3) return;
                const a = sph.computeArea(path);
                areaM2 += (i === 0 ? a : -a);
              });
            });
          }
        } catch { /* area is a nice-to-have; missing geometry just means area=0 */ }

        const attrs = {};
        try {
          e.feature.forEachProperty((value, name) => {
            if (value !== null && value !== undefined && value !== '') attrs[name] = value;
          });
        } catch { /* feature may be detached if removed mid-click */ }
        const provider = pickProviderForLatLng(lng, lat);
        const stateCode = provider?.state || '';

        callbacksRef.current?.deselectAllShapes?.();
        setPropertyInfo({
          lat, lng, areaM2,
          state: stateCode,
          details: normalizeParcelDetails(attrs, stateCode, null),
          address: '',
          addressLoading: true,
        });
        try {
          const geocoder = new window.google.maps.Geocoder();
          const res = await new Promise((resolve) => {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              resolve(status === 'OK' ? results : null);
            });
          });
          const address = res?.[0]?.formatted_address || '';
          const components = res?.[0]?.address_components || null;
          setPropertyInfo((prev) => prev ? {
            ...prev,
            address,
            addressLoading: false,
            details: normalizeParcelDetails(attrs, stateCode, components),
          } : prev);
        } catch {
          setPropertyInfo((prev) => prev ? { ...prev, addressLoading: false } : prev);
        }
      });
      parcelDataLayerRef.current = layer;
    }

    const layer = parcelDataLayerRef.current;

    const clearFeatures = () => {
      // Intentionally do NOT clear selectedParcelIdRef here — it must survive
      // the remove-then-re-add cycle of every WFS refetch so the highlight can
      // be re-applied to the freshly added feature below. The selection ref
      // is only cleared when the panel closes (PropertyInfoPanel.onClose).
      try { layer.forEach((f) => layer.remove(f)); } catch { /* layer may already be torn down */ }
    };

    const fetchParcels = async () => {
      if (!boundariesEnabled || !capabilities?.boundaries) {
        clearFeatures();
        return;
      }
      const map = mapRef.current;
      if (!map) return;
      const zoom = map.getZoom();
      if (zoom == null || zoom < 17) {
        clearFeatures();
        return;
      }
      const bounds = map.getBounds();
      if (!bounds) return;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const centerLng = (ne.lng() + sw.lng()) / 2;
      const centerLat = (ne.lat() + sw.lat()) / 2;
      const provider = pickProviderForLatLng(centerLng, centerLat);
      if (!provider?.buildWfsUrl) {
        clearFeatures();
        return;
      }
      const url = provider.buildWfsUrl([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
      const seq = ++parcelFetchSeqRef.current;
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error(`WFS ${res.status}`);
        const geojson = await res.json();
        if (seq !== parcelFetchSeqRef.current) return;
        clearFeatures();
        if (geojson && geojson.features?.length) {
          layer.addGeoJson(geojson, { idPropertyName: 'OBJECTID' });
          const selId = selectedParcelIdRef.current;
          if (selId != null && typeof layer.__applySelectedStyle === 'function') {
            layer.forEach((f) => {
              if (getStableFeatureId(f) === selId) layer.__applySelectedStyle(f);
            });
          }
        }
      } catch {
        if (seq === parcelFetchSeqRef.current) clearFeatures();
      }
    };

    fetchParcels();
    const idleListener = mapRef.current.addListener('idle', fetchParcels);

    return () => {
      try { window.google.maps.event.removeListener(idleListener); } catch { /* listener may already be gone */ }
      clearFeatures();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, boundariesEnabled, capabilities]);

  // -------------------------------------------------------------------------
  // (2) Toggle data-layer hit-testing while drawing
  // -------------------------------------------------------------------------
  // When the user starts a drawing tool the data layer MUST NOT swallow the
  // click events meant for the drawing manager, or the new shape never gets
  // created. We flip `clickable` off for the duration of any active drawing
  // tool and re-apply the sticky selection highlight when we turn it back on
  // (setStyle resets every feature to the default style).
  useEffect(() => {
    const layer = parcelDataLayerRef.current;
    if (!layer) return;
    const isDrawing = !!drawingMode || !!customTool;
    layer.setStyle({
      fillColor: '#FFEB3B',
      fillOpacity: 0,
      strokeColor: '#FFEB3B',
      strokeWeight: 0,
      strokeOpacity: 0,
      clickable: !isDrawing,
    });
    const selId = selectedParcelIdRef.current;
    if (!isDrawing && selId != null && typeof layer.__applySelectedStyle === 'function') {
      layer.forEach((f) => {
        if (getStableFeatureId(f) === selId) layer.__applySelectedStyle(f);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingMode, customTool]);
}
